/**
 * Geometry for the North Indian chart, kept free of React so the layout maths
 * is unit-testable: a house drawn in the wrong place is invisible in a
 * screenshot and instantly disqualifying to a reader.
 */
import { ZODIAC_SIGNS } from "@/data/zodiac";

export interface Point {
  readonly x: number;
  readonly y: number;
}

/** Two-letter graha labels, in the order a chart is normally read. */
export const PLANET_ABBREV: Readonly<Record<string, string>> = {
  Sun: "Su", Moon: "Mo", Mars: "Ma", Mercury: "Me", Jupiter: "Ju",
  Venus: "Ve", Saturn: "Sa", Rahu: "Ra", Ketu: "Ke",
};

/**
 * Centre of each house in the North Indian diagram, as a fraction of the
 * side. House 1 is the top-centre diamond; the count runs anticlockwise. The
 * board is a square with both diagonals plus the diamond joining the four
 * side midpoints, which carves out exactly twelve regions.
 */
export const NORTH_HOUSE_CENTRES: readonly Point[] = [
  { x: 0.5, y: 0.25 }, { x: 0.26, y: 0.13 }, { x: 0.13, y: 0.26 }, { x: 0.25, y: 0.5 },
  { x: 0.13, y: 0.74 }, { x: 0.26, y: 0.87 }, { x: 0.5, y: 0.75 }, { x: 0.74, y: 0.87 },
  { x: 0.87, y: 0.74 }, { x: 0.75, y: 0.5 }, { x: 0.87, y: 0.26 }, { x: 0.74, y: 0.13 },
];

/** Zero-based index of a sign name, or -1 when unrecognised. */
export function signIndex(sign: string): number {
  const wanted = sign.trim().toLowerCase();
  return ZODIAC_SIGNS.findIndex((s) => s.toLowerCase() === wanted);
}

/** Whole-sign houses: house 1 is the ascendant's sign, each next house the next sign. */
export function signForHouse(ascendantIndex: number, house: number): number {
  return (((ascendantIndex + house - 1) % 12) + 12) % 12;
}

export function houseForSign(ascendantIndex: number, sign: number): number {
  return ((((sign - ascendantIndex) % 12) + 12) % 12) + 1;
}
